import React, { useMemo, useState } from 'react';
import { Col, Row } from 'antd';
import { Input } from 'formik-antd';
import { FormikProps } from 'formik';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import {
  countriesSelector,
  isStatesLoadingSelector,
  statesSelector,
  citiesSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import { WorkerModalFormValues } from '../WorkerModal';

type Props = {
  formik: FormikProps<WorkerModalFormValues>;
};

export default function WorkerStateForm(props: Props) {
  const { formik } = props;
  const [selectedState, setSelectedState] = useState(
    formik.initialValues.state,
  );
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);

  const filteredStates = useMemo(
    () => states.filter((state) => state.countryId === formik.values.country),
    [formik.values.country, states],
  );

  const filteredCities = useMemo(
    () => cities.filter((city) => city.stateId === selectedState),
    [selectedState, cities],
  );

  const handleCountryChange = (
    value: number,
    formik: FormikProps<WorkerModalFormValues>,
  ) => {
    formik.setFieldValue('city', '', false);
    formik.setFieldValue('state', '', false);
    formik.setFieldValue('internationalCityName', '', false);
    formik.setFieldValue('internationalStateName', '', false);
  };

  const handleStateChange = (
    stateId: number,
    formik: FormikProps<WorkerModalFormValues>,
  ) => {
    setSelectedState(stateId);

    formik.setFieldValue('city', '', false);
  };

  return (
    <Row gutter={20}>
      <Col xs={24} md={6}>
        <FormItem name="country" label="Country *">
          <FormikSelectWithLoading
            isLoading={isCountriesLoading}
            name="country"
            showSearch
            placeholder="Select country"
            optionFilterProp="children"
            onChange={(value: number) => handleCountryChange(value, formik)}
          >
            {countries.map((country) => (
              <FormikSelectWithLoading.Option
                key={`select-country-${country.id}`}
                value={country.id || -1}
              >
                {country.name}
              </FormikSelectWithLoading.Option>
            ))}
          </FormikSelectWithLoading>
        </FormItem>
      </Col>

      <Col xs={24} md={6}>
        <FormItem data-id="workerStateField" name="state" label="State *">
          <FormikSelectWithLoading
            isLoading={isStatesLoading}
            name="state"
            showSearch
            placeholder="Select state"
            optionFilterProp="children"
            disabled={!formik.values.country}
            onChange={(value: number) => handleStateChange(value, formik)}
          >
            {filteredStates.map((state) => (
              <FormikSelectWithLoading.Option
                key={`select-state-${state.id}`}
                value={state.id || -1}
              >
                {state.name}
              </FormikSelectWithLoading.Option>
            ))}
          </FormikSelectWithLoading>
        </FormItem>
      </Col>

      <Col xs={24} md={6}>
        <FormItem data-id="workerCityField" name="city" label="City *">
          <FormikAutoCompleteWithLoading
            isLoading={isCitiesLoading}
            name="city"
            filterOption
            disabled={!formik.values.state}
            placeholder="Enter city"
            showSearch
          >
            {filteredCities.map((city) => (
              <FormikAutoCompleteWithLoading.Option
                key={`city-state-${city.id}`}
                value={city.name || ''}
              >
                {city.name}
              </FormikAutoCompleteWithLoading.Option>
            ))}
          </FormikAutoCompleteWithLoading>
        </FormItem>
      </Col>

      <Col xs={24} md={6}>
        <FormItem data-id="workerAddressField" label="Address *" name="address">
          <Input name="address" placeholder="Enter address" autoComplete="off" />
        </FormItem>
      </Col>

      <Col xs={24} md={6}>
        <FormItem data-id="workerZipCodeField" label="ZIP" name="zip">
          <Input name="zip" placeholder="Enter ZIP code" autoComplete="off" />
        </FormItem>
      </Col>
    </Row>
  );
}
